/**
 * UserSetupWizard Component
 * 3-step wizard for inviting users with role + program assignment in one flow.
 * Replaces InviteUserModal with a richer, multi-step experience.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import {
  UserPlus, X, ChevronRight, ChevronLeft, Check,
  Mail, User, FolderKanban, Send, AlertCircle, Shield, Loader2, Phone, PauseCircle,
  Eye, Edit3, Ban,
} from 'lucide-react';
import { apiClient } from '@/services/apiClient';
import { config as productConfig } from '@/src/config';
import { authFetch } from '@/lib/authFetch';
import { MemberPicker, type PickedMember } from '@/components/behavioral-assessment/MemberPicker';
import { MEMBER_ROLE_VALUE, isMemberInvite, resolveInviteRoleType } from '@/utils/memberInvite';
import {
  canOfferSuperAdmin,
  isInternalSystemRole,
  PLATFORM_ORG_ID,
  SUPER_ADMIN_OPTION_LABEL,
} from '@/utils/superAdminPolicy';

// TRUS-513: the Super Admin option is synthetic, not an org role. The RBAC hard
// line forbids role_terminology from mapping to super_admin, so /all-roles never
// returns one — the tier has to be offered as its own value, and the backend's
// invite gate resolves the bare system role.
const SUPER_ADMIN_ROLE_VALUE = 'super_admin';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Normalize a user-entered phone number to E.164 for Twilio 2FA.
 * Guards the failure mode where a US number typed without a country code
 * ("5105143075" or "(510) 514-3075") is stored as "+5105143075" — Twilio
 * reads the "+51" as Peru and the SMS is never delivered.
 *
 *   10 digits                 -> +1XXXXXXXXXX
 *   11 digits starting with 1 -> +1XXXXXXXXXX
 *   other "+<cc>..." numbers  -> preserved (legit international)
 *   empty                     -> '' (caller skips the field)
 */
function normalizePhoneToE164(raw: string): string {
  const trimmed = (raw || '').trim();
  if (!trimmed) return '';
  const hadPlus = trimmed.startsWith('+');
  const digits = trimmed.replace(/\D/g, '');
  if (!digits) return trimmed;
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  if (hadPlus) return `+${digits}`;
  return trimmed;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface UserSetupWizardProps {
  cboId: number;
  /**
   * The active org id with NO fallback applied; null/undefined means the org is
   * not resolved yet. The Super Admin option gates on THIS, never on `cboId`,
   * which callers default to 1 (the platform org) when the org is unknown —
   * turning "we don't know where you are" into the most privileged answer.
   * Absent ⇒ the option is not offered. A privilege gate fails closed.
   */
  resolvedOrgId?: number | null;
  organizationName: string;
  currentUserRole: string;
  onClose: () => void;
  onSuccess: () => void;
}

// /api/cbos/{id}/all-roles returns role_terminology rows with the columns
// role_type / display_name_en / display_name_es — NOT type / display_name.
// The Roles & Permissions table renders display_name_en, so we mirror that.
interface OrgRole {
  id: number;
  role_type: string;
  display_name_en: string;
  display_name_es?: string | null;
  description?: string;
  system_role: string;
  is_organization_role?: boolean;
}

interface OrgProgram {
  id: number;
  name: string;
  description?: string;
  status?: string;
}

// Access levels mirror Role Setup → Programs (RoleProgramAccessDialog) so the
// same two words mean the same thing on both screens. 'full' → can_manage_members
// (view + edit), 'view_only' → visibility only. "No access" is expressed by not
// having an assignment row at all, since row existence is what grants visibility.
type ProgramAccessLevel = 'view_only' | 'full';

interface ProgramAssignment {
  program_id: number;
  program_name: string;
  access_level: ProgramAccessLevel;
}

// A program this user already reaches through their ROLE's configured program
// access (organization_role_programs). Shown read-only so the admin doesn't
// re-grant what the role already carries.
interface InheritedProgram {
  program_id: number;
  program_name: string;
  access_level: 'view_only' | 'full';
}

type WizardStep = 1 | 2 | 3;

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Same wording as Role Setup → Programs (RoleProgramAccessDialog) so the two
// screens describe member access identically.
const PROGRAM_ACCESS_OPTIONS: {
  value: ProgramAccessLevel | 'none';
  label: string;
}[] = [
  { value: 'none', label: 'No access' },
  { value: 'view_only', label: 'View only' },
  { value: 'full', label: 'View + edit' },
];

const SYSTEM_ROLE_LABELS: Record<string, string> = {
  // Healthcare (WeaveIn) roles
  super_admin: 'Super Admin',
  ops_admin: 'Ops Admin',
  org_admin: 'Org Admin',
  program_manager: 'Program Manager',
  supervisor: 'Supervisor',
  staff: 'Staff',
  viewer: 'Viewer',
  // Triveny platform roles — without these the wizard's role badge
  // falls through to displaying the raw role_key (e.g. "tenant_admin"
  // instead of "Tenant Admin").
  platform_admin: 'Platform Admin',
  tenant_admin: 'Tenant Admin',
  tenant_user: 'Team Member',
  tenant_viewer: 'Read-only Viewer',
};

// ---------------------------------------------------------------------------
// Step Indicator
// ---------------------------------------------------------------------------

function StepIndicator({ currentStep }: { currentStep: WizardStep }) {
  // Triveny invite is a 2-step flow (User → Review) because there are no
  // programs to assign on the platform side.
  const steps = (productConfig.isTriveny
    ? [
        { num: 1 as WizardStep, label: 'User' },
        { num: 3 as WizardStep, label: 'Review' },
      ]
    : [
        { num: 1 as WizardStep, label: 'User' },
        { num: 2 as WizardStep, label: 'Programs' },
        { num: 3 as WizardStep, label: 'Review' },
      ]);

  return (
    <div className="flex items-center justify-center gap-0 mb-6">
      {steps.map((s, idx) => {
        const isActive = currentStep === s.num;
        const isCompleted = currentStep > s.num;

        return (
          <React.Fragment key={s.num}>
            {/* Connector line before (except first) */}
            {idx > 0 && (
              <div
                className={`h-0.5 w-12 sm:w-16 transition-colors ${
                  isCompleted || isActive ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'
                }`}
              />
            )}

            {/* Step circle + label */}
            <div className="flex flex-col items-center gap-1">
              <div
                className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold transition-colors ${
                  isCompleted
                    ? 'bg-green-500 text-white'
                    : isActive
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                }`}
              >
                {isCompleted ? <Check className="w-4 h-4" /> : s.num}
              </div>
              <span
                className={`text-xs font-medium ${
                  isActive
                    ? 'text-blue-600 dark:text-blue-400'
                    : isCompleted
                    ? 'text-green-600 dark:text-green-400'
                    : 'text-gray-400 dark:text-gray-500'
                }`}
              >
                {s.label}
              </span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export function UserSetupWizard({
  cboId,
  resolvedOrgId,
  organizationName,
  currentUserRole,
  onClose,
  onSuccess,
}: UserSetupWizardProps) {
  const { t } = useTranslation();

  // ---- Wizard state ----
  const [step, setStep] = useState<WizardStep>(1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ---- Step 1 form state ----
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [exemptFrom2fa, setExemptFrom2fa] = useState(false);
  const [selectedRole, setSelectedRole] = useState('');
  const [additionalRoles, setAdditionalRoles] = useState<string[]>([]);
  // TRUS-734: the roster member a Member invite attaches to. Without it the
  // backend matches by email and creates a new member only if nobody matches.
  const [linkedMember, setLinkedMember] = useState<PickedMember | null>(null);

  // ---- Send-now toggle (Review step) ----
  // false = stage the invitation in 'held' state without emailing the user,
  // so the onboarding team can train them first and release the invite later.
  const [sendNow, setSendNow] = useState(true);

  // ---- Step 2 form state ----
  const [programAssignments, setProgramAssignments] = useState<ProgramAssignment[]>([]);

  // ---- Fetched data ----
  const [orgRoles, setOrgRoles] = useState<OrgRole[]>([]);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [programs, setPrograms] = useState<OrgProgram[]>([]);
  const [programsLoading, setProgramsLoading] = useState(false);
  const [inheritedPrograms, setInheritedPrograms] = useState<InheritedProgram[]>([]);

  // ---- Derived ----
  // Triveny platform_admin and WeaveIn super_admin are peers at the top
  // of their respective access tiers — both can invite at the highest
  // role (platform_admin / super_admin respectively) without restriction.
  const isSuperAdmin = currentUserRole === 'super_admin' || currentUserRole === 'platform_admin';

  const headers = useMemo(
    () => ({ 'X-CBO-ID': cboId.toString() }),
    [cboId],
  );

  // TRUS-513: Super Admin may be offered only inside the platform organization
  // (CBO 1) and only to a super admin. Triveny's top tier is platform_admin —
  // a different product's role — so that product's behaviour is left alone.
  // Gates on resolvedOrgId, NOT cboId: app.tsx passes `activeOrgId || 1`, so an
  // unhydrated org arrives here as the platform org and would open this gate in
  // whatever workspace the admin is actually looking at (reported on Somos).
  // `resolvedOrgId` is null while unresolved, and canOfferSuperAdmin(null, …)
  // is false — unknown org means no option.
  const canPickSuperAdmin = productConfig.isTriveny
    ? isSuperAdmin
    : canOfferSuperAdmin(resolvedOrgId ?? null, currentUserRole);

  // Filter org roles for visibility. An org role mapped to an internal tier is
  // never offered, for anybody: org roles must not map to super_admin/ops_admin
  // (the RBAC hard line), but legacy rows exist — Somos Mayfair had an org
  // "Super Admin" that a DB trigger then sprayed across other orgs. Picking one
  // here would promote the invitee to a platform tier through the org path.
  const visibleOrgRoles = useMemo(
    () => orgRoles.filter((r) => !isInternalSystemRole(r.system_role)),
    [orgRoles],
  );

  // Roles available as additional (exclude selected primary)
  const additionalRoleOptions = useMemo(() => {
    return visibleOrgRoles.filter((r) => {
      const primaryKey = `orgRole:${selectedRole}`;
      // If selectedRole is an orgRole id, exclude it
      return String(r.id) !== selectedRole && r.role_type !== selectedRole;
    });
  }, [visibleOrgRoles, selectedRole]);

  // ---- Validation ----
  const isEmailValid = EMAIL_REGEX.test(email);
  const isStep1Valid = isEmailValid && name.trim().length > 0 && selectedRole.length > 0;

  // ---- Data fetching: org roles on mount ----
  useEffect(() => {
    let cancelled = false;
    setRolesLoading(true);

    const isTriveny = productConfig.isTriveny;

    // On Triveny we fetch from /api/platform/roles (platform_shared.
    // platform_role_definitions) and adapt to the OrgRole shape so the
    // existing wizard UI just works. The healthcare /all-roles endpoint
    // returns role_terminology entries scoped by cbo_id, which don't
    // exist for Triveny tenants.
    const fetchPromise: Promise<OrgRole[]> = isTriveny
      ? authFetch(`${productConfig.weaveinApi.baseUrl}/api/platform/roles`)
          .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
          .then((rows: any[]) =>
            rows
              .filter((r) => r.is_active)
              .map((r) => ({
                // Use a stable hash of the UUID as an int id so the wizard's
                // String(id) compares stay consistent. role_type carries the
                // real UUID so saveInvite can pass it back to the BE.
                id:
                  parseInt(String(r.id).replace(/-/g, '').slice(0, 8), 16) &
                  0x7fffffff,
                role_type: r.role_key,
                display_name_en: r.display_name,
                display_name_es: null,
                description: r.description || undefined,
                system_role: r.role_key,
                is_organization_role: r.is_system,
              })),
          )
      : apiClient
          .get(`/api/cbos/${cboId}/all-roles`, { headers })
          .then((res) => res.data || []);

    fetchPromise
      .then((roles: OrgRole[]) => {
        if (cancelled) return;
        setOrgRoles(roles);
        // Set default selected role. Pick tenant_admin / org_admin (the
        // org-level admin tier) by preference rather than the absolute
        // highest role — platform_admin / super_admin should be an
        // explicit choice, not the default.
        // TRUS-513: the default must never be an internal tier — an invite sent
        // without touching the dropdown must not hand out platform access.
        const filtered = roles.filter(
          (r) =>
            r.system_role !== 'super_admin' &&
            r.system_role !== 'ops_admin' &&
            r.system_role !== 'platform_admin',
        );
        if (filtered.length > 0) {
          const defaultRole =
            filtered.find((r) => r.system_role === 'tenant_admin') ||
            filtered.find((r) => r.system_role === 'org_admin') ||
            filtered.find((r) => r.system_role !== 'platform_admin' && r.system_role !== 'super_admin') ||
            filtered[0];
          setSelectedRole(String(defaultRole.id));
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('Failed to fetch org roles:', err);
        }
      })
      .finally(() => {
        if (!cancelled) setRolesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [cboId, headers, isSuperAdmin]);

  // ---- Data fetching: programs when entering step 2 ----
  useEffect(() => {
    if (step !== 2) return;
    if (programs.length > 0) return; // Already fetched

    let cancelled = false;
    setProgramsLoading(true);

    // /api/organizations/{id}/programs doesn't exist (405). The canonical
    // route is /api/cbo/{id}/programs (singular cbo).
    apiClient
      .get(`/api/cbo/${cboId}/programs`, { headers })
      .then((programsRes) => {
        if (cancelled) return;
        const programsData = programsRes.data;
        // /api/cbo/{id}/programs may return either an array or { programs: [...] }
        setPrograms(
          Array.isArray(programsData)
            ? programsData
            : programsData?.programs || []
        );
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('Failed to fetch programs:', err);
        }
      })
      .finally(() => {
        if (!cancelled) setProgramsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [step, cboId, headers, programs.length]);

  // ---- Data fetching: the SELECTED ROLE's configured program access ----
  // Role Setup → Programs (organization_role_programs) already grants member
  // visibility/edit to every holder of the role, with no per-user row. Without
  // showing it here, an admin has no way to know the programs they are about to
  // pick are ones the role already covers — which is what made this step feel
  // like re-entering Step 1's answer.
  //
  // NOTE: resolveRoleType is declared below this effect, so the role_type is
  // resolved inline — referencing that useCallback in the dep array would read
  // it before initialization (TDZ) during render.
  useEffect(() => {
    if (step !== 2 || productConfig.isTriveny) return;
    const roleType = orgRoles.find((r) => String(r.id) === selectedRole)?.role_type;
    if (!roleType) {
      setInheritedPrograms([]);
      return;
    }

    let cancelled = false;
    apiClient
      .get(`/api/organizations/${cboId}/roles/${encodeURIComponent(roleType)}/programs`, {
        headers,
      })
      .then((res) => {
        if (cancelled) return;
        const rows = Array.isArray(res.data) ? res.data : [];
        setInheritedPrograms(
          rows
            .filter((r: any) => r.access_level === 'full' || r.access_level === 'view_only')
            .map((r: any) => ({
              program_id: r.program_id,
              program_name: r.program_name,
              access_level: r.access_level,
            })),
        );
      })
      .catch(() => {
        // Non-fatal: the step still works, it just can't show inherited access.
        if (!cancelled) setInheritedPrograms([]);
      });

    return () => {
      cancelled = true;
    };
  }, [step, cboId, headers, selectedRole, orgRoles]);

  // ---- Helpers ----

  // TRUS-513: the Super Admin option is a synthetic value with no org-role row
  // behind it, so every roleId -> display lookup has to recognise it explicitly
  // or the Review step would show the raw key "super_admin".
  const isSuperAdminSelection = selectedRole === SUPER_ADMIN_ROLE_VALUE;

  const getRoleDisplayName = useCallback(
    (roleId: string): string => {
      if (roleId === SUPER_ADMIN_ROLE_VALUE) return SUPER_ADMIN_OPTION_LABEL;
      if (roleId === MEMBER_ROLE_VALUE) return t('admin.member_invite.role_option');
      const role = orgRoles.find((r) => String(r.id) === roleId);
      return role?.display_name_en || roleId;
    },
    [orgRoles, t],
  );

  const getRoleSystemBadge = useCallback(
    (roleId: string): string => {
      if (roleId === SUPER_ADMIN_ROLE_VALUE) return SYSTEM_ROLE_LABELS.super_admin || 'Super Admin';
      if (roleId === MEMBER_ROLE_VALUE) return t('admin.member_invite.role_badge');
      const role = orgRoles.find((r) => String(r.id) === roleId);
      return SYSTEM_ROLE_LABELS[role?.system_role || ''] || role?.system_role || '';
    },
    [orgRoles, t],
  );

  const getAdditionalRoleDisplayName = useCallback(
    (roleType: string): string => {
      const role = orgRoles.find((r) => r.role_type === roleType);
      return role?.display_name_en || roleType;
    },
    [orgRoles],
  );

  const resolveSystemRole = useCallback(
    (roleId: string): string => {
      const role = orgRoles.find((r) => String(r.id) === roleId);
      return role?.system_role || roleId;
    },
    [orgRoles],
  );

  // The invitation's primary role must be the ORG role_type (e.g. 'dcm01'), NOT
  // the system_role — the backend keys user_organizations.role + the
  // role_terminology lookup off role_type. Sending system_role collapsed custom
  // roles like "Disaster Case Manager" (dcm01) down to "staff" (TRUS role bug).
  // TRUS-734: resolveInviteRoleType also maps the built-in Member option to
  // the bare 'member' role the backend's member branch keys on.
  const resolveRoleType = useCallback(
    (roleId: string): string => resolveInviteRoleType(roleId, orgRoles),
    [orgRoles],
  );

  // TRUS-734: the same rule as the backend's _is_member_invite, so the
  // existing-member picker shows exactly when the server takes its member branch.
  const isMemberRole = isMemberInvite(selectedRole, orgRoles);

  const pickLinkedMember = (picked: PickedMember | null) => {
    setLinkedMember(picked);
    setError(null);
    if (picked) {
      setName(picked.name);
      if (picked.email) setEmail(picked.email);
    }
  };

  // Set a program's access level. 'none' removes the assignment entirely —
  // row existence is what grants visibility, so "no access" is "no row".
  const setProgramAccess = useCallback(
    (program: OrgProgram, level: ProgramAccessLevel | 'none') => {
      setProgramAssignments((prev) => {
        const without = prev.filter((pa) => pa.program_id !== program.id);
        if (level === 'none') return without;
        return [
          ...without,
          {
            program_id: program.id,
            program_name: program.name,
            access_level: level,
          },
        ];
      });
    },
    [],
  );

  // ---- Navigation ----

  const goNext = () => {
    setError(null);
    if (step === 1 && isStep1Valid) {
      // Triveny has no concept of program assignment — skip directly to
      // review/send. Step 2 (program list + assignment matrix) calls a
      // healthcare-specific endpoint and would render an empty matrix.
      setStep(productConfig.isTriveny ? 3 : 2);
    } else if (step === 2) {
      setStep(3);
    }
  };

  const goBack = () => {
    setError(null);
    // Single decision, not two: the previous form ran `if (step === 3) setStep(2)`
    // after the Triveny branch, and both read the same stale `step`, so the last
    // write won and Triveny users going Back from Review landed on the Programs
    // step that flow deliberately skips.
    if (step === 2) setStep(1);
    else if (step === 3) setStep(productConfig.isTriveny ? 1 : 2);
  };

  const skipPrograms = () => {
    setError(null);
    setProgramAssignments([]);
    setStep(3);
  };

  // ---- Submit ----

  const handleSubmit = async () => {
    if (submitting) return;
    setError(null);
    setSubmitting(true);

    try {
      const payload: Record<string, any> = {
        email,
        name,
        role: resolveRoleType(selectedRole),
        send_now: sendNow,
      };

      // Normalize to E.164 before sending so a US number typed without a
      // country code ("5105143075") isn't stored as "+5105143075" — which
      // Twilio routes as Peru (+51) and the 2FA SMS never delivers. The
      // backend re-normalizes too; this keeps client + server in agreement.
      const trimmedPhone = normalizePhoneToE164(phoneNumber);
      if (trimmedPhone) {
        payload.phone_number = trimmedPhone;
      }

      // When the admin exempts the user from 2FA, send require_2fa=false so the
      // backend seeds an admin exemption at registration (overrides org
      // enforcement) and the user never has to enroll. Omit otherwise to keep
      // the default (2FA required once a phone is present).
      if (exemptFrom2fa) {
        payload.require_2fa = false;
      }

      if (additionalRoles.length > 0) {
        payload.additional_roles = additionalRoles;
      }

      if (isMemberRole && linkedMember) {
        payload.member_id = linkedMember.id;
      }

      // Only the two fields the backend actually enforces: the row itself
      // (visibility on the program's members) and can_manage_members (edit).
      // organization_role_id / is_program_lead / can_enroll_members were
      // collected here but read by no authorization path, so sending them only
      // recorded intent that never took effect.
      if (programAssignments.length > 0) {
        payload.program_assignments = programAssignments.map((pa) => ({
          program_id: pa.program_id,
          can_manage_members: pa.access_level === 'full',
        }));
      }

      await apiClient.post(`/api/cbos/${cboId}/users/invite`, payload, {
        headers,
      });
      onSuccess();
    } catch (err: any) {
      console.error('Failed to invite user:', err);
      const detail = err.response?.data?.detail;
      // TRUS-734: several roster members share this email, so the backend
      // won't guess who gets the login. Send the admin back to pick one.
      if (err.response?.status === 409 && detail?.code === 'multiple_member_matches') {
        setError(
          t('admin.member_invite.multiple_matches', { count: detail.candidate_count ?? 2 }),
        );
        setStep(1);
        setSubmitting(false);
        return;
      }
      const errorMessage =
        (typeof detail === 'string' ? detail : detail?.message) ||
        err.response?.data?.message ||
        err.message ||
        'Failed to send invitation';
      setError(errorMessage);
      setSubmitting(false);
    }
  };

  // ---- Render helpers ----

  const renderError = () => {
    if (!error) return null;
    return (
      <div className="flex items-center gap-2 px-3 py-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-600 dark:text-red-400 text-sm mb-4">
        <AlertCircle className="w-4 h-4 flex-shrink-0" />
        <span>{error}</span>
      </div>
    );
  };

  // ===========================================================================
  // Step 1 — Identity & Roles
  // ===========================================================================

  const renderStep1 = () => {
    const selectedOrgRole = visibleOrgRoles.find(
      (r) => String(r.id) === selectedRole,
    );
    const roleDescription =
      (selectedRole === MEMBER_ROLE_VALUE ? t('admin.member_invite.role_description') : '') ||
      selectedOrgRole?.description ||
      (selectedOrgRole
        ? `Maps to system role: ${SYSTEM_ROLE_LABELS[selectedOrgRole.system_role] || selectedOrgRole.system_role}`
        : '');

    return (
      <div className="space-y-5">
        {/* TRUS-734: link a Member invite to the roster record it is for, so
            registration doesn't attach the login to a new empty duplicate. */}
        {isMemberRole && (
          <div>
            <MemberPicker
              id="wizard-link-member"
              value={linkedMember}
              onChange={pickLinkedMember}
              label={t('admin.member_invite.link_label')}
              placeholder={t('admin.member_invite.link_placeholder')}
              showEmail
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {linkedMember
                ? t('admin.member_invite.linked_to', { name: linkedMember.name })
                : t('admin.member_invite.link_hint')}
            </p>
          </div>
        )}

        {/* Email */}
        <div>
          <label
            htmlFor="wizard-email"
            className="flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5"
          >
            <Mail className="w-4 h-4" />
            <span>Email Address *</span>
          </label>
          <input
            id="wizard-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="user@example.com"
            disabled={submitting}
            className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm transition-colors focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 dark:focus:ring-blue-900 disabled:bg-gray-50 dark:bg-gray-700 dark:text-gray-100 disabled:cursor-not-allowed"
          />
          {email.length > 0 && !isEmailValid && (
            <p className="mt-1 text-xs text-red-500">Please enter a valid email address</p>
          )}
        </div>

        {/* Name */}
        <div>
          <label
            htmlFor="wizard-name"
            className="flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5"
          >
            <User className="w-4 h-4" />
            <span>Full Name *</span>
          </label>
          <input
            id="wizard-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter user's full name"
            disabled={submitting}
            className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm transition-colors focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 dark:focus:ring-blue-900 disabled:bg-gray-50 dark:bg-gray-700 dark:text-gray-100 disabled:cursor-not-allowed"
          />
        </div>

        {/* Personal mobile for 2FA (NOT an org sender number) */}
        <div>
          <label
            htmlFor="wizard-phone"
            className="flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5"
          >
            <Phone className="w-4 h-4" />
            <span>User's Personal Mobile (for 2FA) <span className="text-gray-400 font-normal">(optional)</span></span>
          </label>
          <input
            id="wizard-phone"
            type="tel"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            placeholder="+18085551234"
            disabled={submitting}
            className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm transition-colors focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 dark:focus:ring-blue-900 disabled:bg-gray-50 dark:bg-gray-700 dark:text-gray-100 disabled:cursor-not-allowed"
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            E.164 format. This must be the user's <strong>own mobile</strong> — a
            number they can receive an SMS code on at registration. Do not enter
            an organization-owned number here, or 2FA setup will deadlock
            (the code would go to a phone the user can't yet access).
            Organization sender numbers are assigned separately under
            Org Settings → Phone Numbers → Staff Assignment.
          </p>
        </div>

        {/* 2FA exemption */}
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
          <label className="flex items-start gap-2.5 cursor-pointer">
            <input
              type="checkbox"
              checked={exemptFrom2fa}
              onChange={(e) => setExemptFrom2fa(e.target.checked)}
              disabled={submitting}
              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:cursor-not-allowed"
            />
            <span>
              <span className="flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-300">
                <Shield className="w-4 h-4" />
                Exempt this user from two-factor authentication (2FA)
              </span>
              <span className="mt-1 block text-xs text-gray-500 dark:text-gray-400">
                The user will never be prompted for a 2FA code at login — no
                enrollment required. This exemption overrides an
                organization-wide 2FA requirement. Leave unchecked to keep 2FA
                required (the default).
              </span>
            </span>
          </label>
        </div>

        {/* Primary Role */}
        <div>
          <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            <Shield className="w-4 h-4" />
            <span>Primary Role *</span>
          </label>

          {rolesLoading ? (
            <div className="flex items-center gap-2 py-3 text-sm text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading roles...
            </div>
          ) : (
            <Select
              value={selectedRole}
              onValueChange={(val) => {
                setSelectedRole(val);
                // Remove from additional roles if it was there
                const matchedRole = orgRoles.find((r) => String(r.id) === val);
                if (matchedRole) {
                  setAdditionalRoles((prev) =>
                    prev.filter((rt) => rt !== matchedRole.role_type),
                  );
                }
              }}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select a role..." />
              </SelectTrigger>
              <SelectContent>
                {/* TRUS-513: Super Admin is offered ONLY here, only in the
                    platform organization, and only to a super admin. Selecting
                    it grants nothing on its own — the backend parks the
                    invitation until a SECOND super admin approves it.
                    ops_admin is never listed: it is assigned through the
                    platform admin console, and POST /invite rejects it with 400
                    regardless of caller role. The option is synthetic because
                    role_terminology may not map to super_admin, so /all-roles
                    has no row to render. */}
                {canPickSuperAdmin && (
                  <>
                    <div className="px-3 py-1.5 text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                      Internal Roles
                    </div>
                    {/* textValue tells the Select component to display ONLY the
                        role name in the closed trigger (not the full children,
                        which would double up the name and the badge because
                        extractTextFromChildren concatenates both spans). */}
                    <SelectItem
                      value={SUPER_ADMIN_ROLE_VALUE}
                      textValue={SUPER_ADMIN_OPTION_LABEL}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-gray-900 dark:text-gray-100">{SUPER_ADMIN_OPTION_LABEL}</span>
                        <span className="ml-auto inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300">
                          Needs approval
                        </span>
                      </div>
                    </SelectItem>
                  </>
                )}

                {/* Organization roles */}
                <div className="px-3 py-1.5 text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  {organizationName} Roles
                </div>
                {/* visibleOrgRoles already drops internal tiers (TRUS-513). */}
                {visibleOrgRoles
                  .map((role) => (
                    // textValue: see the Internal Roles SelectItem above. Without
                    // it the trigger renders "Org Admin Org Admin" — display
                    // name concatenated with the system-role badge.
                    <SelectItem key={role.id} value={String(role.id)} textValue={role.display_name_en}>
                      <div className="flex items-center gap-2">
                        <span className="text-gray-900 dark:text-gray-100">{role.display_name_en}</span>
                        <span className="ml-auto inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
                          {SYSTEM_ROLE_LABELS[role.system_role] || role.system_role}
                        </span>
                      </div>
                    </SelectItem>
                  ))}

                {/* TRUS-734: built-in, independent of /all-roles — no org has a
                    member role row, so without this a member could not be
                    invited from here at all. Value is the bare 'member' role
                    the backend's member branch keys on. */}
                <div className="px-3 py-1.5 text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  {t('admin.member_invite.role_group')}
                </div>
                <SelectItem value={MEMBER_ROLE_VALUE} textValue={t('admin.member_invite.role_option')}>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-900 dark:text-gray-100">{t('admin.member_invite.role_option')}</span>
                    <span className="ml-auto inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300">
                      {t('admin.member_invite.role_badge')}
                    </span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          )}

          {/* TRUS-513: say plainly that nothing is granted yet. Otherwise the
              admin reads "Send Invitation", sees a success toast, and waits for
              an email that only goes out once a second super admin approves. */}
          {isSuperAdminSelection && (
            <div className="mt-2 flex gap-2 px-3 py-2 rounded-lg text-xs leading-relaxed bg-amber-50 text-amber-900 border border-amber-200 dark:bg-amber-900/30 dark:text-amber-200 dark:border-amber-800">
              <Shield className="w-4 h-4 shrink-0 mt-0.5" />
              <span>
                Platform Admin grants full access to every organization. No invitation is
                sent yet — this raises a request that a <strong>second super admin</strong>{' '}
                must approve first.
              </span>
            </div>
          )}

          {/* Role description */}
          {roleDescription && (
            <div className="mt-2 px-3 py-2 bg-gray-50 dark:bg-gray-700 rounded-lg text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
              {roleDescription}
            </div>
          )}
        </div>

        {/* Additional Roles */}
        {additionalRoleOptions.length > 0 && (
          <div>
            <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              <Shield className="w-4 h-4" />
              <span>Additional Roles</span>
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              Optionally assign additional roles to this user.
            </p>
            <div className="space-y-1 border border-gray-200 dark:border-gray-600 rounded-lg p-3 max-h-40 overflow-auto">
              {additionalRoleOptions.map((role) => {
                const isChecked = additionalRoles.includes(role.role_type);
                return (
                  <label
                    key={role.id}
                    className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 p-1.5 rounded transition-colors"
                  >
                    <Checkbox
                      checked={isChecked}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setAdditionalRoles((prev) => [...prev, role.role_type]);
                        } else {
                          setAdditionalRoles((prev) =>
                            prev.filter((rt) => rt !== role.role_type),
                          );
                        }
                      }}
                    />
                    <span className="text-sm text-gray-900 dark:text-gray-100">
                      {role.display_name_en}
                    </span>
                    <span className="ml-auto inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                      {SYSTEM_ROLE_LABELS[role.system_role] || role.system_role}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  // ===========================================================================
  // Step 2 — Program Assignment
  // ===========================================================================

  const renderStep2 = () => {
    if (programsLoading) {
      return (
        <div className="flex flex-col items-center justify-center py-12 text-gray-500 dark:text-gray-400">
          <Loader2 className="w-6 h-6 animate-spin mb-3" />
          <p className="text-sm">Loading programs...</p>
        </div>
      );
    }

    if (programs.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-12 text-gray-500 dark:text-gray-400">
          <FolderKanban className="w-10 h-10 mb-3 opacity-40" />
          <p className="text-sm font-medium">No programs configured</p>
          <p className="text-xs mt-1">
            You can skip this step and assign programs later.
          </p>
        </div>
      );
    }

    const roleName = getRoleDisplayName(selectedRole);

    return (
      <div className="space-y-4">
        {/* What the ROLE already grants — read-only. */}
        {inheritedPrograms.length > 0 ? (
          <div className="rounded-lg border border-green-200 dark:border-green-800 bg-green-50/60 dark:bg-green-900/10 p-3">
            <div className="flex items-start gap-2">
              <Shield className="w-4 h-4 mt-0.5 text-green-600 dark:text-green-400 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-green-900 dark:text-green-200">
                  Already granted by the {roleName} role
                </p>
                <ul className="mt-1.5 space-y-1">
                  {inheritedPrograms.map((ip) => (
                    <li
                      key={ip.program_id}
                      className="flex items-center gap-2 text-xs text-green-800 dark:text-green-300"
                    >
                      {ip.access_level === 'full' ? (
                        <Edit3 className="w-3 h-3 flex-shrink-0" />
                      ) : (
                        <Eye className="w-3 h-3 flex-shrink-0" />
                      )}
                      <span className="truncate">{ip.program_name}</span>
                      <span className="text-green-600 dark:text-green-400">
                        · {ip.access_level === 'full' ? 'View + edit' : 'View only'}
                      </span>
                    </li>
                  ))}
                </ul>
                <p className="mt-2 text-xs text-green-700 dark:text-green-400">
                  No action needed for these — change them under Role Setup →
                  Programs, where they apply to everyone with this role.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-600 dark:text-gray-400">
            The {roleName} role has no program access configured, so this user
            needs at least one program here to see any members.
          </p>
        )}

        <div>
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Additional program access
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Only for programs this user needs beyond their role. Leave empty if
            the role already covers them.
          </p>
        </div>

        <div className="space-y-2 max-h-[360px] overflow-auto pr-1">
          {programs.map((program) => {
            const assignment = programAssignments.find(
              (pa) => pa.program_id === program.id,
            );
            const inherited = inheritedPrograms.find(
              (ip) => ip.program_id === program.id,
            );
            const level: ProgramAccessLevel | 'none' = assignment?.access_level ?? 'none';
            const isAssigned = level !== 'none';

            return (
              <div
                key={program.id}
                className={`flex items-center gap-3 border rounded-lg p-3 transition-colors ${
                  isAssigned
                    ? 'border-blue-300 dark:border-blue-700 bg-blue-50/50 dark:bg-blue-900/10'
                    : 'border-gray-200 dark:border-gray-600'
                }`}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
                    {program.name}
                  </p>
                  {inherited ? (
                    <p className="text-xs text-green-700 dark:text-green-400 mt-0.5">
                      Role already grants{' '}
                      {inherited.access_level === 'full' ? 'view + edit' : 'view only'}
                    </p>
                  ) : (
                    program.description && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-1">
                        {program.description}
                      </p>
                    )
                  )}
                </div>

                <Select
                  value={level}
                  onValueChange={(val) =>
                    setProgramAccess(program, val as ProgramAccessLevel | 'none')
                  }
                >
                  <SelectTrigger className="w-[150px] text-sm flex-shrink-0">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PROGRAM_ACCESS_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value} textValue={opt.label}>
                        <div className="flex items-center gap-2">
                          {opt.value === 'none' && <Ban className="w-3.5 h-3.5 text-gray-400" />}
                          {opt.value === 'view_only' && <Eye className="w-3.5 h-3.5" />}
                          {opt.value === 'full' && <Edit3 className="w-3.5 h-3.5" />}
                          <span>{opt.label}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            );
          })}
        </div>

        {/* Honest caveat: 'View only' here suppresses the per-assignment edit
            grant, but a role with no explicit member-edit page configuration
            still edits by default (TRUS-308), so view-only is only guaranteed
            once the role is opted out under Role Setup → Pages. Saying so beats
            a label that silently means the opposite. */}
        {programAssignments.some((pa) => pa.access_level === 'view_only') && (
          <div className="flex items-start gap-2 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50/60 dark:bg-amber-900/10 p-3">
            <AlertCircle className="w-4 h-4 mt-0.5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
            <p className="text-xs text-amber-800 dark:text-amber-300">
              <strong>View only</strong> removes this assignment's edit grant, but
              roles can still edit member profiles by default. To make it truly
              read-only, turn off edit access for the{' '}
              {getRoleDisplayName(selectedRole)} role under Role Setup → Pages
              (Member Profile).
            </p>
          </div>
        )}
      </div>
    );
  };

  // ===========================================================================
  // Step 3 — Review & Confirm
  // ===========================================================================

  const renderStep3 = () => {
    return (
      <div className="space-y-4">
        <Card className="border border-gray-200 dark:border-gray-600">
          <CardContent className="p-4 space-y-4">
            {/* User info */}
            <div>
              <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                User Details
              </h4>
              <div className="space-y-1.5">
                <div className="flex items-center gap-2 text-sm">
                  <User className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-900 dark:text-gray-100 font-medium">
                    {name}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-700 dark:text-gray-300">{email}</span>
                </div>
                {phoneNumber.trim() && (
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-700 dark:text-gray-300">{phoneNumber.trim()}</span>
                    <span className="text-xs text-gray-500">(personal mobile for 2FA)</span>
                  </div>
                )}
              </div>
            </div>

            {/* Divider */}
            <div className="border-t border-gray-200 dark:border-gray-700" />

            {/* Roles */}
            <div>
              <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                Roles
              </h4>
              <div className="flex flex-wrap gap-2">
                <Badge variant="primary" size="sm">
                  {getRoleDisplayName(selectedRole)}
                </Badge>
                {/* BUG-003: only show the system-role badge when it adds
                    information — i.e. when display_name_en is a custom term
                    that maps to a known system role (e.g. "Care Navigator"
                    mapping to "Staff"). For system-named roles like "Ops
                    Admin" / "Org Admin", display_name_en and the system-role
                    label are identical and we'd render the same word twice
                    (which is what the wizard was doing on Project AngelFood
                    sandbox). Case-insensitive compare. */}
                {(() => {
                  const primary = getRoleDisplayName(selectedRole) || '';
                  const systemBadge = getRoleSystemBadge(selectedRole) || '';
                  if (!systemBadge) return null;
                  if (primary.trim().toLowerCase() === systemBadge.trim().toLowerCase()) return null;
                  return (
                    <Badge variant="info" size="sm">
                      {systemBadge}
                    </Badge>
                  );
                })()}
              </div>

              {additionalRoles.length > 0 && (
                <div className="mt-2">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    Additional:
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {additionalRoles.map((rt) => (
                      <Badge key={rt} variant="secondary" size="sm">
                        {getAdditionalRoleDisplayName(rt)}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Program Assignments */}
            {programAssignments.length > 0 && (
              <>
                <div className="border-t border-gray-200 dark:border-gray-700" />
                <div>
                  <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                    Program Assignments
                  </h4>
                  <div className="space-y-2">
                    {programAssignments.map((pa) => (
                      <div
                        key={pa.program_id}
                        className="flex items-center gap-2 p-2.5 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
                      >
                        <FolderKanban className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                        <span className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
                          {pa.program_name}
                        </span>
                        <Badge
                          variant={pa.access_level === 'full' ? 'info' : 'secondary'}
                          size="sm"
                        >
                          {pa.access_level === 'full' ? 'View + edit' : 'View only'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {programAssignments.length === 0 && !productConfig.isTriveny && (
              <>
                <div className="border-t border-gray-200 dark:border-gray-700" />
                <div>
                  <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                    Program Assignments
                  </h4>
                  {inheritedPrograms.length > 0 ? (
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      None beyond the role — {getRoleDisplayName(selectedRole)}{' '}
                      already grants access to{' '}
                      {inheritedPrograms.map((ip) => ip.program_name).join(', ')}.
                    </p>
                  ) : (
                    <p className="text-sm text-gray-400 dark:text-gray-500 italic">
                      No programs assigned — can be configured later.
                    </p>
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Send-now / hold toggle. TRUS-513: for a Platform Admin request the
            toggle has no meaning — the backend parks the invitation either way
            until a second super admin approves — so it is replaced by the
            explanation rather than left showing a promise it can't keep. */}
        {isSuperAdminSelection ? (
          <Card className="border border-amber-200 dark:border-amber-800">
            <CardContent className="p-4">
              <h4 className="text-sm font-medium text-amber-900 dark:text-amber-200 flex items-center gap-1.5">
                <Shield className="w-4 h-4" />
                Awaiting a second super admin
              </h4>
              <p className="text-xs text-amber-800 dark:text-amber-300 mt-1">
                Submitting raises a Platform Admin request. Nothing is emailed and no access
                is granted until another super admin approves it from Super Admin Approvals.
              </p>
            </CardContent>
          </Card>
        ) : (
        <Card className="border border-gray-200 dark:border-gray-600">
          <CardContent className="p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 flex items-center gap-1.5">
                  {sendNow ? <Send className="w-4 h-4" /> : <PauseCircle className="w-4 h-4" />}
                  {sendNow ? 'Send invitation now' : 'Hold invitation'}
                </h4>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {sendNow
                    ? 'The user gets an email immediately and can register right away.'
                    : 'Stages the invitation without emailing the user — release it manually from the user list after training is complete.'}
                </p>
              </div>
              <Switch
                checked={sendNow}
                onCheckedChange={setSendNow}
                disabled={submitting}
              />
            </div>
          </CardContent>
        </Card>
        )}
      </div>
    );
  };

  // ===========================================================================
  // Footer buttons per step
  // ===========================================================================

  const renderFooter = () => {
    switch (step) {
      case 1:
        return (
          <div className="flex justify-between gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-5 py-2.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={goNext}
              disabled={!isStep1Valid || submitting}
              className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 border border-transparent rounded-lg text-sm font-medium text-white hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        );

      case 2:
        return (
          <div className="flex justify-between gap-3">
            <button
              type="button"
              onClick={goBack}
              disabled={submitting}
              className="flex items-center gap-2 px-5 py-2.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
              Back
            </button>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={skipPrograms}
                disabled={submitting}
                className="px-5 py-2.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Skip
              </button>
              <button
                type="button"
                onClick={goNext}
                disabled={submitting}
                className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 border border-transparent rounded-lg text-sm font-medium text-white hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="flex justify-between gap-3">
            <button
              type="button"
              onClick={goBack}
              disabled={submitting}
              className="flex items-center gap-2 px-5 py-2.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
              Back
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting}
              className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 border border-transparent rounded-lg text-sm font-medium text-white hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {sendNow ? 'Sending...' : 'Saving...'}
                </>
              ) : (
                <>
                  {sendNow ? <Send className="w-4 h-4" /> : <PauseCircle className="w-4 h-4" />}
                  {sendNow ? 'Send Invitation' : 'Save & Hold Invite'}
                </>
              )}
            </button>
          </div>
        );
    }
  };

  // ===========================================================================
  // Main render
  // ===========================================================================

  const stepTitles: Record<WizardStep, string> = {
    1: 'User Identity & Roles',
    2: 'Program Assignment',
    3: 'Review & Confirm',
  };

  return (
    <div
      className="fixed inset-0 bg-white dark:bg-gray-900 z-[10000] flex"
    >
      <div
        className="bg-white dark:bg-gray-800 w-full h-full overflow-auto"
      >
       <div className="max-w-3xl mx-auto w-full">
        {/* Header */}
        <div className="flex justify-between items-start p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex gap-3">
            <UserPlus className="w-6 h-6 text-blue-600 flex-shrink-0" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Set Up New User
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Invite a user to join {organizationName}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-gray-500 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6">
          {/* Step Indicator */}
          <StepIndicator currentStep={step} />

          {/* Step title */}
          <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200 mb-4">
            {stepTitles[step]}
          </h3>

          {/* Error */}
          {renderError()}

          {/* Step content */}
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          {renderFooter()}
        </div>
       </div>
      </div>
    </div>
  );
}
